import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";

type ApiErrorContextValue = {
  hasApiError: boolean;
  resetApiError: () => void;
};

const ApiErrorContext = createContext<ApiErrorContextValue | undefined>(undefined);

const API_ERROR_EVENT = "api-error";
const API_SUCCESS_EVENT = "api-success";
const API_ROUTE_CHANGE_EVENT = "api-route-change";

export const ApiErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasApiError, setHasApiError] = useState(false);
  const navIdRef = useRef(0);
  const hasApiErrorRef = useRef(false);
  const hasSuccessForNavRef = useRef(false);

  const resetApiError = useCallback(() => {
    setHasApiError(false);
  }, []);

  useEffect(() => {
    hasApiErrorRef.current = hasApiError;
  }, [hasApiError]);

  useEffect(() => {
    const handleApiError = (event: Event) => {
      const { navId } = (event as CustomEvent<{ navId?: number }>).detail || {};
      if (navId !== navIdRef.current) {
        return;
      }
      if (hasSuccessForNavRef.current) {
        return;
      }
      setHasApiError(true);
    };

    const handleApiSuccess = (event: Event) => {
      const { navId } = (event as CustomEvent<{ navId?: number }>).detail || {};
      if (navId !== navIdRef.current) {
        return;
      }
      hasSuccessForNavRef.current = true;
      setHasApiError(false);
    };

    const handleRouteChange = () => {
      navIdRef.current += 1;
      hasSuccessForNavRef.current = false;
      setHasApiError(false);
    };

    window.addEventListener(API_ERROR_EVENT, handleApiError);
    window.addEventListener(API_SUCCESS_EVENT, handleApiSuccess);
    window.addEventListener(API_ROUTE_CHANGE_EVENT, handleRouteChange);

    return () => {
      window.removeEventListener(API_ERROR_EVENT, handleApiError);
      window.removeEventListener(API_SUCCESS_EVENT, handleApiSuccess);
      window.removeEventListener(API_ROUTE_CHANGE_EVENT, handleRouteChange);
    };
  }, []);

  useEffect(() => {
    const win = window as typeof window & {
      __apiErrorFetchPatched?: boolean;
      __apiErrorAxiosPatched?: boolean;
      __apiErrorHistoryPatched?: boolean;
    };

    if (!win.__apiErrorFetchPatched) {
      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        if (hasApiErrorRef.current) {
          const pausedError = new Error("API requests paused due to previous failure.");
          (pausedError as Error & { __apiErrorPaused?: boolean }).__apiErrorPaused = true;
          return Promise.reject(pausedError);
        }
        const navId = navIdRef.current;
        try {
          const response = await originalFetch(...args);
          if (!response.ok && response.status >= 500) {
            window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail: { navId } }));
          } else if (response.ok) {
            window.dispatchEvent(new CustomEvent(API_SUCCESS_EVENT, { detail: { navId } }));
          }
          return response;
        } catch (error) {
          window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail: { navId } }));
          throw error;
        }
      };
      win.__apiErrorFetchPatched = true;
    }

    if (!win.__apiErrorAxiosPatched) {
      axios.interceptors.request.use(config => {
        if (hasApiErrorRef.current) {
          const pausedError = new Error("API requests paused due to previous failure.");
          (pausedError as Error & { __apiErrorPaused?: boolean }).__apiErrorPaused = true;
          return Promise.reject(pausedError);
        }
        (config as typeof config & { __apiErrorNavId?: number }).__apiErrorNavId =
          navIdRef.current;
        return config;
      });

      axios.interceptors.response.use(
        response => {
          const navId = (response.config as typeof response.config & { __apiErrorNavId?: number })
            .__apiErrorNavId;
          window.dispatchEvent(new CustomEvent(API_SUCCESS_EVENT, { detail: { navId } }));
          return response;
        },
        error => {
          if ((error as Error & { __apiErrorPaused?: boolean })?.__apiErrorPaused) {
            return Promise.reject(error);
          }
          const status = error?.response?.status;
          const navId =
            (error?.config as typeof error.config & { __apiErrorNavId?: number })
              ?.__apiErrorNavId ?? navIdRef.current;
          if (!status || status >= 500) {
            window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail: { navId } }));
          }
          return Promise.reject(error);
        }
      );
      win.__apiErrorAxiosPatched = true;
    }

    if (!win.__apiErrorHistoryPatched) {
      const originalPushState = window.history.pushState.bind(window.history);
      const originalReplaceState = window.history.replaceState.bind(window.history);

      window.history.pushState = (...args) => {
        const result = originalPushState(...args);
        window.dispatchEvent(new CustomEvent(API_ROUTE_CHANGE_EVENT));
        return result;
      };

      window.history.replaceState = (...args) => {
        const result = originalReplaceState(...args);
        window.dispatchEvent(new CustomEvent(API_ROUTE_CHANGE_EVENT));
        return result;
      };

      window.addEventListener("popstate", () => {
        window.dispatchEvent(new CustomEvent(API_ROUTE_CHANGE_EVENT));
      });

      win.__apiErrorHistoryPatched = true;
    }
  }, []);

  const value = useMemo(
    () => ({
      hasApiError,
      resetApiError,
    }),
    [hasApiError, resetApiError]
  );

  return <ApiErrorContext.Provider value={value}>{children}</ApiErrorContext.Provider>;
};

export default ApiErrorContext;
